import { parseDraftValues } from "helpers/requestFormHelper";
import type { RequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { buildClassicScheduleFromStops } from "@RequestFormV2/utils/buildClassicScheduleFromStops";
import { stripUnselectedPackingListRows } from "@RequestFormV2/utils/stripUnselectedPackingListRows";
import { stripUnselectedScheduleAddresses } from "@RequestFormV2/utils/stripUnselectedScheduleAddresses";

const SHIPMENT_NULLIFY_FIELDS = [
  "quoting_deadline_date",
  "quoting_deadline_time",
  "confirmation_date",
  "confirmation_time",
] as const;

const SHIPMENT_EMPTY_STRING_FIELDS = ["title", "comments"] as const;

const SERVICE_NULLIFY_FIELDS = ["incoterms", "insurance_instructions"] as const;

const SERVICE_EMPTY_STRING_FIELDS = ["incoterm_instructions"] as const;

const PACKING_LIST_NULLIFY_FIELDS = [
  "additional_information",
  "cbm_scope",
  "crating_required_by",
  "dangerous_good_value",
  "handling_unit",
  "nmfc_class",
  "temperature_control_value",
  "un_number",
] as const;

function nullifyIfEmpty(value: string | undefined): string | null | undefined {
  if (value === "") return null;
  return value;
}

function nullifyEmptyStringsDeep(value: unknown): unknown {
  if (value === "") return null;
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map(nullifyEmptyStringsDeep);
  }

  return Object.fromEntries(Object.entries(value).map(([key, entryValue]) => [key, nullifyEmptyStringsDeep(entryValue)]));
}

function nullifyObjectFields(
  obj: Record<string, unknown> | undefined,
  fields: readonly string[],
): Record<string, unknown> | undefined {
  if (!obj) return obj;

  fields.forEach((field) => {
    if (obj[field] === "") {
      obj[field] = null;
    }
  });

  return obj;
}

function coerceNullToEmptyString(
  obj: Record<string, unknown> | undefined,
  fields: readonly string[],
): Record<string, unknown> | undefined {
  if (!obj) return obj;

  fields.forEach((field) => {
    if (obj[field] === null) {
      obj[field] = "";
    }
  });

  return obj;
}

function isAnsweredSpecialRequirement(value: unknown): boolean {
  return value === true || value === "true" || value === false || value === "false";
}

function stripUnansweredSpecialRequirements(data: Record<string, unknown>): Record<string, unknown> {
  const service = data.service as Record<string, unknown> | undefined;
  if (!service?.special_requirements || typeof service.special_requirements !== "object") {
    return data;
  }

  const requirements = service.special_requirements as Record<string, { value?: unknown }>;
  const answeredEntries = Object.entries(requirements).filter(([, entry]) =>
    isAnsweredSpecialRequirement(entry?.value),
  );

  if (answeredEntries.length === Object.keys(requirements).length) {
    return data;
  }

  const next = { ...data, service: { ...service } };
  if (answeredEntries.length === 0) {
    const { special_requirements: _specialRequirements, ...serviceWithoutRequirements } = service;
    next.service = serviceWithoutRequirements;
  } else {
    next.service = { ...service, special_requirements: Object.fromEntries(answeredEntries) };
  }

  return next;
}

function pickTopLevelKeys(data: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  return Object.fromEntries(keys.filter((key) => key in data).map((key) => [key, data[key]]));
}

function normalizeEmptyStringsForSystemDraft(payload: Record<string, unknown>, keys?: string[]) {
  const includes = (key: string) => !keys || keys.includes(key);

  if (includes("organizational")) {
    const organizational = payload.organizational as Record<string, unknown> | undefined;
    if (organizational) {
      organizational.invoice_location = nullifyIfEmpty(organizational.invoice_location as string | undefined);
      // Draft create treats null as missing for mandatory custom cost-recovery fields;
      // empty string satisfies the check (same workaround as RequestForm V1).
      coerceNullToEmptyString(organizational, [
        "custom_cost_recovery_field1",
        "custom_cost_recovery_field2",
      ]);
    }
  }

  if (includes("shipment")) {
    coerceNullToEmptyString(payload.shipment as Record<string, unknown>, SHIPMENT_EMPTY_STRING_FIELDS);
    nullifyObjectFields(payload.shipment as Record<string, unknown>, SHIPMENT_NULLIFY_FIELDS);
  }

  if (includes("service")) {
    coerceNullToEmptyString(payload.service as Record<string, unknown>, SERVICE_EMPTY_STRING_FIELDS);
    nullifyObjectFields(payload.service as Record<string, unknown>, SERVICE_NULLIFY_FIELDS);
  }

  if (includes("packing_list")) {
    const packingList = payload.packing_list;
    if (Array.isArray(packingList)) {
      packingList.forEach((row) => {
        if (row && typeof row === "object") {
          nullifyObjectFields(row as Record<string, unknown>, PACKING_LIST_NULLIFY_FIELDS);
        }
      });
    }
  }

  if (includes("schedule") && payload.schedule !== undefined) {
    payload.schedule = nullifyEmptyStringsDeep(payload.schedule);
  }

  return payload;
}

function mapQuickRequestDraftPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const shipment = payload.shipment as Record<string, unknown> | undefined;
  if (!shipment) return payload;

  const { title, comments, ...remainingShipment } = shipment;
  const hasTitle = title !== undefined;
  const hasComments = comments !== undefined;

  if (!hasTitle && !hasComments) {
    return payload;
  }

  const next: Record<string, unknown> = { ...payload };

  next.quick_request = {
    ...(hasTitle ? { title: title ?? "" } : {}),
    ...(hasComments ? { comments: comments ?? "" } : {}),
  };

  if (Object.keys(remainingShipment).length > 0) {
    next.shipment = remainingShipment;
  } else {
    delete next.shipment;
  }

  return next;
}

export function prepareDraftPayload(
  formValues: Record<string, unknown>,
  keys?: string[],
  mode: RequestFormMode = "quote",
) {
  // Omit read-only / non-editable draft metadata. Sending external_id:null on
  // NextUI edit drafts clears SaveDraft.additional_identifier (shipment ref).
  const {
    draftSaved: _draftSaved,
    external_id: _externalId,
    draft_reference_number: _draftReferenceNumber,
    ...rest
  } = formValues;
  const scoped = keys ? pickTopLevelKeys(rest, keys) : rest;

  let data = scoped;
  if (!keys || keys.includes("service")) {
    data = stripUnansweredSpecialRequirements(data);
  }

  const parsed = parseDraftValues(data) as Record<string, unknown>;

  let result = parsed;
  if (!keys || keys.includes("packing_list")) {
    result = stripUnselectedPackingListRows(parsed);
  }

  const normalized = normalizeEmptyStringsForSystemDraft(result, keys);

  if (!keys || keys.includes("schedule")) {
    let withSchedule = stripUnselectedScheduleAddresses(normalized);
    if (mode === "quick" && withSchedule.schedule && typeof withSchedule.schedule === "object") {
      withSchedule = {
        ...withSchedule,
        schedule: buildClassicScheduleFromStops(withSchedule.schedule as Record<string, unknown>),
      };
    }
    return mode === "quick" ? mapQuickRequestDraftPayload(withSchedule) : withSchedule;
  }

  return mode === "quick" ? mapQuickRequestDraftPayload(normalized) : normalized;
}
